import { element } from "@groundspark/browser-ui";

/** The server rejects longer destinations. */
const MAXIMUM_DESTINATION_BYTES = 2048;
const SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;
const INVALID = "Enter a web address starting with https://, such as https://example.org/join.";

/**
 * Where scanning a leaflet's QR code leads: an HTTPS web address. An address
 * typed without a scheme is taken to be HTTPS. Throws with the reason it is invalid.
 */
export function qrDestination(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") throw new Error("Enter the web address that the QR code should open.");
  const address = SCHEME.test(trimmed) ? trimmed : `https://${trimmed}`;
  if (!URL.canParse(address)) throw new Error(INVALID);
  const url = new URL(address);
  if (
    url.protocol !== "https:" ||
    url.hostname === "" ||
    url.username !== "" ||
    url.password !== "" ||
    new TextEncoder().encode(url.href).length > MAXIMUM_DESTINATION_BYTES
  )
    throw new Error(INVALID);
  return url.href;
}

/** A field for a QR code's destination, explaining what scanning does. */
export function destinationField(value: string): {
  readonly label: HTMLLabelElement;
  readonly input: HTMLInputElement;
} {
  const input = element("input", "field__control");
  input.name = "destination";
  input.type = "text";
  input.inputMode = "url";
  input.setAttribute("autocomplete", "url");
  input.spellcheck = false;
  input.required = true;
  input.placeholder = "https://";
  input.value = value;
  const label = element(
    "label",
    "field",
    element("span", "field__label", "QR code destination"),
    input,
    element(
      "span",
      "muted",
      "Scanning the QR code counts a scan, then opens this web address. You can change it at any time, even after printing.",
    ),
  );
  return { label, input };
}
